/**
 * Purpose: Teacher's Desk "Ask for Help" button displayed during the question phase.
 * Owns: Help request button rendering, available/used visual states, hint text display.
 * Notes: When tapped, reveals the question's hint and (for MC) eliminates one wrong answer.
 *        Single use per question. The scoring engine applies the age-group knockdown
 *        multiplier automatically — this component only handles the UI interaction.
 *        Styled to match the chalkboard/classroom aesthetic (CS-22: theme tokens only).
 */

interface TeachersDeskProps {
  /** Whether the help has already been used for the current question */
  used: boolean;
  /** Whether the button should be disabled (e.g., answer already selected) */
  disabled: boolean;
  /** Hint text to display after help is requested (null if not yet revealed) */
  hintText: string | null;
  /** Called when the player taps "Ask for Help" */
  onRequestHelp: () => void;
}

export function TeachersDesk({ used, disabled, hintText, onRequestHelp }: TeachersDeskProps) {
  return (
    <div style={styles.wrapper}>
      {/* Help request button — styled as a wooden desk plaque */}
      <button
        style={{
          ...styles.button,
          ...(used ? styles.buttonUsed : {}),
        }}
        onClick={onRequestHelp}
        disabled={used || disabled}
        aria-label={used ? 'Help already used for this question' : 'Ask the teacher for help'}
      >
        <span style={styles.icon} aria-hidden="true">
          {used ? '\u2705' : '\uD83C\uDF4E'}
        </span>
        <span style={styles.label}>
          {used ? 'Help Used' : "Teacher's Desk"}
        </span>
      </button>

      {/* Hint text — chalk-styled speech bubble shown after help is requested */}
      {hintText && (
        <div
          className="banner-pop"
          style={styles.hintBubble}
          role="status"
          aria-label="Teacher's hint"
        >
          <span style={styles.hintIcon} aria-hidden="true">{'\uD83D\uDCA1'}</span>
          <p className="chalk-text" style={styles.hintText}>{hintText}</p>
        </div>
      )}
    </div>
  );
}

/* Styles (CS-22: theme tokens only) */
const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.5rem',
    width: '100%',
    maxWidth: '440px',
    margin: '0 auto',
  },
  /* Button styled as a wooden desk plaque with chalk lettering */
  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.5rem 1rem',
    border: '2px solid var(--accent-wood)',
    borderRadius: '8px',
    background: 'rgba(107, 66, 38, 0.25)',
    color: 'var(--accent-gold)',
    fontFamily: 'var(--font-family-handwriting)',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s ease, transform 0.1s ease',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
  },
  /* Dimmed state after help is used */
  buttonUsed: {
    opacity: 0.5,
    cursor: 'default',
    color: 'var(--muted)',
    borderColor: 'var(--muted)',
  },
  icon: {
    fontSize: '1.1rem',
    lineHeight: 1,
  },
  label: {
    lineHeight: 1.2,
  },
  /* Hint bubble — appears below the button with a chalk-on-board look */
  hintBubble: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    padding: '0.6rem 1rem',
    borderRadius: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    border: '1px solid rgba(245, 240, 225, 0.15)',
    width: '100%',
  },
  hintIcon: {
    fontSize: '1rem',
    lineHeight: 1.4,
    flexShrink: 0,
  },
  hintText: {
    fontSize: '0.9rem',
    lineHeight: 1.4,
    fontFamily: 'var(--font-family-handwriting)',
    opacity: 0.85,
    margin: 0,
  },
};
